import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { CandidateSelector } from "@/components/candidates/CandidateSelector";
import { JobSelector } from "@/components/jobs/JobSelector";
import {
	WorkforceFormData,
	WORKFORCE_STATUSES,
	BILLING_TYPES,
	LOCATION_TYPES,
	CONTRACT_TYPES,
	CURRENCY_OPTIONS,
	calculateMargin,
} from "@/types/workforce";
import { createWorkforce } from "@/hooks/workforce/useWorkforceActions";
import { supabase } from "@/integrations/supabase/client";
import {
	Loader2,
	X,
	Users2,
	Building,
	Calendar,
	DollarSign,
	MapPin,
	FileText,
	TrendingUp,
	AlertTriangle,
} from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";

interface AddWorkforceModalProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	onSuccess?: () => void;
}

interface SelectedCandidate {
	id: string;
	name: string;
}

interface SelectedJob {
	id: string;
	title: string;
	company_name?: string;
}

export const AddWorkforceModal = ({ open, onOpenChange, onSuccess }: AddWorkforceModalProps) => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [organizationUuid, setOrganizationUuid] = useState<string | null>(null);
	const [activeTab, setActiveTab] = useState<'assignment' | 'work' | 'financial'>('assignment');
	const [mobileTab, setMobileTab] = useState<'form' | 'summary'>('form');

	// Form state
	const [selectedCandidate, setSelectedCandidate] = useState<SelectedCandidate | null>(null);
	const [useInternalJob, setUseInternalJob] = useState(true);
	const [selectedJob, setSelectedJob] = useState<SelectedJob | null>(null);

	const [formData, setFormData] = useState<WorkforceFormData>({
		candidate_id: "",
		job_id: null,
		external_job_title: "",
		external_company_name: "",
		external_company_contact: "",
		external_job_description: "",
		start_date: new Date().toISOString().split('T')[0],
		end_date: "",
		expected_end_date: "",
		status: "active",
		purchase_price_daily: "",
		purchase_price_hourly: "",
		selling_price_daily: "",
		selling_price_hourly: "",
		currency: "EUR",
		billing_type: undefined,
		location: "",
		location_type: undefined,
		work_percentage: "",
		contract_type: undefined,
		contract_reference: "",
		assigned_recruiter: "",
		notes: "",
	});

	// Calculate margins
	const dailyMargin = useMemo(() => {
		const purchase = parseFloat(formData.purchase_price_daily || '0');
		const selling = parseFloat(formData.selling_price_daily || '0');
		return calculateMargin(purchase, selling);
	}, [formData.purchase_price_daily, formData.selling_price_daily]);

	const hourlyMargin = useMemo(() => {
		const purchase = parseFloat(formData.purchase_price_hourly || '0');
		const selling = parseFloat(formData.selling_price_hourly || '0');
		return calculateMargin(purchase, selling);
	}, [formData.purchase_price_hourly, formData.selling_price_hourly]);

	// Fetch organization UUID
	useEffect(() => {
		const fetchOrgUuid = async () => {
			const { data: { user } } = await supabase.auth.getUser();
			if (!user) return;

			const { data: userOrg } = await supabase
				.from('user_organization')
				.select('organization_uuid')
				.eq('user_uuid', user.id)
				.single();

			if (userOrg?.organization_uuid)
				setOrganizationUuid(userOrg.organization_uuid);
		};

		fetchOrgUuid();
	}, []);

	const handleCandidateSelect = (candidate: { id: string; name: string }) => {
		setSelectedCandidate(candidate);
		setFormData(prev => ({ ...prev, candidate_id: candidate.id }));
	};

	const handleJobSelect = (job: { id: string; title: string; company_name?: string }) => {
		setSelectedJob(job);
		setFormData(prev => ({ ...prev, job_id: job.id }));
	};

	const handleInputChange = (field: keyof WorkforceFormData, value: string) => {
		setFormData(prev => ({ ...prev, [field]: value }));
	};

	const resetForm = () => {
		setSelectedCandidate(null);
		setSelectedJob(null);
		setUseInternalJob(true);
		setActiveTab('assignment');
		setMobileTab('form');
		setFormData({
			candidate_id: "",
			job_id: null,
			external_job_title: "",
			external_company_name: "",
			external_company_contact: "",
			external_job_description: "",
			start_date: new Date().toISOString().split('T')[0],
			end_date: "",
			expected_end_date: "",
			status: "active",
			purchase_price_daily: "",
			purchase_price_hourly: "",
			selling_price_daily: "",
			selling_price_hourly: "",
			currency: "EUR",
			billing_type: undefined,
			location: "",
			location_type: undefined,
			work_percentage: "",
			contract_type: undefined,
			contract_reference: "",
			assigned_recruiter: "",
			notes: "",
		});
	};

	const handleClose = () => {
		resetForm();
		onOpenChange(false);
	};

	const handleSubmit = async () => {
		if (!organizationUuid) {
			toast({
				title: t("workforce.errors.noOrganization", "Error"),
				description: t("workforce.errors.noOrganizationDesc", "Could not determine organization"),
				variant: "destructive",
			});
			return;
		}

		if (!formData.candidate_id) {
			toast({
				title: t("workforce.errors.noCandidate", "Consultant required"),
				description: t("workforce.errors.noCandidateDesc", "Please select a consultant"),
				variant: "destructive",
			});
			return;
		}

		if (!formData.start_date) {
			toast({
				title: t("workforce.errors.noStartDate", "Start date required"),
				description: t("workforce.errors.noStartDateDesc", "Please enter a start date"),
				variant: "destructive",
			});
			return;
		}

		// Validate job info
		if (useInternalJob && !formData.job_id) {
			toast({
				title: t("workforce.errors.noJob", "Job required"),
				description: t("workforce.errors.noJobDesc", "Please select a job or switch to external assignment"),
				variant: "destructive",
			});
			return;
		}

		if (!useInternalJob && !formData.external_company_name) {
			toast({
				title: t("workforce.errors.noCompany", "Company required"),
				description: t("workforce.errors.noCompanyDesc", "Please enter a company name for external assignment"),
				variant: "destructive",
			});
			return;
		}

		if (!useInternalJob && !formData.external_job_title) {
			toast({
				title: t("workforce.errors.noJobTitle", "Role required"),
				description: t("workforce.errors.noJobTitleDesc", "Please enter a role/position for external assignment"),
				variant: "destructive",
			});
			return;
		}

		setIsSubmitting(true);

		try {
			// Prepare form data
			const submitData: WorkforceFormData = {
				...formData,
				job_id: useInternalJob ? formData.job_id : null,
				external_job_title: useInternalJob ? "" : formData.external_job_title,
				external_company_name: useInternalJob ? "" : formData.external_company_name,
				external_company_contact: useInternalJob ? "" : formData.external_company_contact,
				external_job_description: useInternalJob ? "" : formData.external_job_description,
			};

			const workforceId = await createWorkforce(submitData, organizationUuid);

			if (workforceId) {
				resetForm();
				onOpenChange(false);
				if (onSuccess) onSuccess();
				navigate(`/workforce/${workforceId}`);
			}
		} finally {
			setIsSubmitting(false);
		}
	};

	const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
		if (e.target === e.currentTarget)
			handleClose();
	};

	if (!open) return null;

	return (
		<div
			className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm animate-fade-in p-2 sm:p-4"
			onClick={handleBackdropClick}
		>
			<div className="bg-white dark:bg-gray-900 rounded-sm shadow-2xl w-full max-w-6xl max-h-[95vh] flex flex-col animate-slide-in border border-gray-200 dark:border-gray-700 overflow-hidden">
				{/* Header */}
				<div className="flex justify-between items-center p-4 sm:p-6 border-b border-gray-200 dark:border-gray-700 bg-gradient-to-r from-gray-50 to-white dark:from-gray-800 dark:to-gray-900 flex-shrink-0">
					<div className="flex items-center gap-3">
						<div className="w-10 h-10 bg-primary rounded-sm flex items-center justify-center shadow-sm">
							<Users2 className="h-5 w-5 text-primary-foreground" />
						</div>
						<div>
							<h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">
								{t("workforce.add.title", "Add Workforce Assignment")}
							</h2>
							<p className="text-sm text-muted-foreground">
								{t("workforce.add.subtitle", "Create a new consultant assignment")}
							</p>
						</div>
					</div>
					<button
						type="button"
						onClick={handleClose}
						className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors p-2 rounded-sm hover:bg-gray-100 dark:hover:bg-gray-800"
					>
						<X className="h-5 w-5" />
					</button>
				</div>

				{/* Mobile Tab Navigation */}
				<div className="xl:hidden border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 flex-shrink-0">
					<div className="flex">
						<button
							type="button"
							onClick={() => setMobileTab('form')}
							className={cn(
								"flex-1 px-4 py-3 text-sm font-medium border-b-2 transition-colors",
								mobileTab === 'form'
									? "border-primary text-primary bg-muted"
									: "border-transparent text-muted-foreground hover:text-foreground hover:bg-muted"
							)}
						>
							<div className="flex items-center justify-center gap-2">
								<FileText className="h-4 w-4" />
								{t("workforce.add.formTab", "Details")}
							</div>
						</button>
						<button
							type="button"
							onClick={() => setMobileTab('summary')}
							className={cn(
								"flex-1 px-4 py-3 text-sm font-medium border-b-2 transition-colors",
								mobileTab === 'summary'
									? "border-primary text-primary bg-muted"
									: "border-transparent text-muted-foreground hover:text-foreground hover:bg-muted"
							)}
						>
							<div className="flex items-center justify-center gap-2">
								<TrendingUp className="h-4 w-4" />
								{t("workforce.add.summaryTab", "Summary")}
							</div>
						</button>
					</div>
				</div>

				<div className="flex flex-col xl:flex-row flex-1 min-h-0 overflow-hidden">
					{/* Main Content - Left Side */}
					<div className={cn(
						"flex-1 border-r border-gray-200 dark:border-gray-700 min-h-0",
						"xl:block",
						mobileTab === 'form' ? "block" : "hidden"
					)}>
						<Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as 'assignment' | 'work' | 'financial')} className="w-full h-full flex flex-col">
							<TabsList className="mx-4 mt-4 grid grid-cols-3">
								<TabsTrigger value="assignment" className="flex items-center gap-2">
									<Users2 className="h-4 w-4" />
									<span className="hidden sm:inline">{t("workforce.add.tabs.assignment", "Assignment")}</span>
								</TabsTrigger>
								<TabsTrigger value="work" className="flex items-center gap-2">
									<MapPin className="h-4 w-4" />
									<span className="hidden sm:inline">{t("workforce.add.tabs.work", "Work Details")}</span>
								</TabsTrigger>
								<TabsTrigger value="financial" className="flex items-center gap-2">
									<DollarSign className="h-4 w-4" />
									<span className="hidden sm:inline">{t("workforce.add.tabs.financial", "Financial")}</span>
								</TabsTrigger>
							</TabsList>

							{/* Assignment Tab */}
							<TabsContent value="assignment" className="flex-1 overflow-y-auto p-4 space-y-5">
								{/* Consultant Selection */}
								<div className="space-y-2">
									<Label className="text-sm font-medium text-gray-700 dark:text-gray-300">
										{t("workforce.fields.consultant", "Consultant")} *
									</Label>
									<CandidateSelector
										onSelectCandidate={handleCandidateSelect}
										buttonClassName="w-full h-11"
									/>
									{selectedCandidate && (
										<div className="flex items-center gap-2 p-2 bg-muted rounded-sm border">
											<Users2 className="h-4 w-4 text-muted-foreground" />
											<span className="text-sm font-medium">{selectedCandidate.name}</span>
										</div>
									)}
								</div>

								{/* Job Type Toggle */}
								<div className="p-4 border rounded-sm bg-gray-50 dark:bg-gray-800 dark:border-gray-700">
									<div className="flex items-center justify-between">
										<div>
											<Label className="text-sm font-medium">{t("workforce.add.jobType", "Assignment Type")}</Label>
											<p className="text-sm text-muted-foreground mt-1">
												{useInternalJob
													? t("workforce.add.internalJob", "Link to existing job in system")
													: t("workforce.add.externalJob", "External assignment (client not in system)")}
											</p>
										</div>
										<Switch
											checked={useInternalJob}
											onCheckedChange={setUseInternalJob}
										/>
									</div>
								</div>

								{/* Job Selection or External Job Details */}
								{useInternalJob ? (
									<div className="space-y-2">
										<Label className="text-sm font-medium text-gray-700 dark:text-gray-300">
											{t("workforce.fields.job", "Job")} *
										</Label>
										<JobSelector
											onSelectJob={handleJobSelect}
											selectedJobId={formData.job_id || undefined}
											buttonClassName="w-full h-11"
										/>
										{selectedJob && (
											<div className="flex items-center gap-2 p-2 bg-blue-50 dark:bg-blue-900/30 rounded-sm border border-blue-200 dark:border-blue-800">
												<Building className="h-4 w-4 text-blue-500" />
												<span className="text-sm font-medium text-blue-700 dark:text-blue-300">
													{selectedJob.title} {selectedJob.company_name && `@ ${selectedJob.company_name}`}
												</span>
											</div>
										)}
									</div>
								) : (
									<div className="space-y-4 p-4 border rounded-sm bg-muted/50">
										<div className="flex items-center gap-2 mb-2">
											<Building className="h-4 w-4 text-muted-foreground" />
											<span className="text-sm font-semibold">{t("workforce.add.externalDetails", "External Client Details")}</span>
										</div>
										<div className="space-y-2">
											<Label className="text-sm font-medium">{t("workforce.fields.externalCompany", "Company Name")} *</Label>
											<Input
												value={formData.external_company_name}
												onChange={(e) => handleInputChange("external_company_name", e.target.value)}
												placeholder={t("workforce.placeholders.companyName", "Enter company name")}
												className="h-11"
											/>
										</div>
										<div className="space-y-2">
											<Label className="text-sm font-medium">{t("workforce.fields.externalJobTitle", "Role/Position")} *</Label>
											<Input
												value={formData.external_job_title}
												onChange={(e) => handleInputChange("external_job_title", e.target.value)}
												placeholder={t("workforce.placeholders.jobTitle", "Enter role/position")}
												className="h-11"
											/>
										</div>
										<div className="space-y-2">
											<Label className="text-sm font-medium">{t("workforce.fields.clientContact", "Client Contact")}</Label>
											<Input
												value={formData.external_company_contact}
												onChange={(e) => handleInputChange("external_company_contact", e.target.value)}
												placeholder={t("workforce.placeholders.clientContact", "Contact name, email, or phone")}
												className="h-11"
											/>
										</div>
									</div>
								)}

								{/* Dates */}
								<div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
									<div className="space-y-2">
										<Label className="text-sm font-medium text-gray-700 dark:text-gray-300">
											<Calendar className="h-4 w-4 inline mr-1" />
											{t("workforce.fields.startDate", "Start Date")} *
										</Label>
										<Input
											type="date"
											value={formData.start_date}
											onChange={(e) => handleInputChange("start_date", e.target.value)}
											className="h-11"
										/>
									</div>
									<div className="space-y-2">
										<Label className="text-sm font-medium text-gray-700 dark:text-gray-300">
											<Calendar className="h-4 w-4 inline mr-1" />
											{t("workforce.fields.expectedEndDate", "Expected End Date")}
										</Label>
										<Input
											type="date"
											value={formData.expected_end_date}
											onChange={(e) => handleInputChange("expected_end_date", e.target.value)}
											className="h-11"
										/>
									</div>
								</div>

								{/* Status */}
								<div className="space-y-2">
									<Label className="text-sm font-medium text-gray-700 dark:text-gray-300">{t("workforce.fields.status", "Status")}</Label>
									<Select
										value={formData.status}
										onValueChange={(value) => handleInputChange("status", value)}
									>
										<SelectTrigger className="h-11">
											<SelectValue />
										</SelectTrigger>
										<SelectContent>
											{WORKFORCE_STATUSES.map((status) => (
												<SelectItem key={status.value} value={status.value}>
													{status.label}
												</SelectItem>
											))}
										</SelectContent>
									</Select>
								</div>
							</TabsContent>

							{/* Work Details Tab */}
							<TabsContent value="work" className="flex-1 overflow-y-auto p-4 space-y-5">
								{/* Location */}
								<div className="space-y-2">
									<Label className="text-sm font-medium text-gray-700 dark:text-gray-300">{t("workforce.fields.location", "Work Location")}</Label>
									<Input
										value={formData.location}
										onChange={(e) => handleInputChange("location", e.target.value)}
										placeholder={t("workforce.placeholders.location", "City, office, or remote")}
										className="h-11"
									/>
								</div>

								<div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
									{/* Location Type */}
									<div className="space-y-2">
										<Label className="text-sm font-medium text-gray-700 dark:text-gray-300">{t("workforce.fields.locationType", "Work Mode")}</Label>
										<Select
											value={formData.location_type || ""}
											onValueChange={(value) => handleInputChange("location_type", value)}
										>
											<SelectTrigger className="h-11">
												<SelectValue placeholder={t("workforce.placeholders.selectWorkMode", "Select...")} />
											</SelectTrigger>
											<SelectContent>
												{LOCATION_TYPES.map((type) => (
													<SelectItem key={type.value} value={type.value}>
														{type.label}
													</SelectItem>
												))}
											</SelectContent>
										</Select>
									</div>

									{/* Work Percentage */}
									<div className="space-y-2">
										<Label className="text-sm font-medium text-gray-700 dark:text-gray-300">{t("workforce.fields.workPercentage", "Allocation %")}</Label>
										<Input
											type="number"
											min="0"
											max="100"
											value={formData.work_percentage}
											onChange={(e) => handleInputChange("work_percentage", e.target.value)}
											placeholder="100"
											className="h-11"
										/>
									</div>
								</div>

								{/* Contract Type */}
								<div className="space-y-2">
									<Label className="text-sm font-medium text-gray-700 dark:text-gray-300">{t("workforce.fields.contractType", "Contract Type")}</Label>
									<Select
										value={formData.contract_type || ""}
										onValueChange={(value) => handleInputChange("contract_type", value)}
									>
										<SelectTrigger className="h-11">
											<SelectValue placeholder={t("workforce.placeholders.selectContract", "Select...")} />
										</SelectTrigger>
										<SelectContent>
											{CONTRACT_TYPES.map((type) => (
												<SelectItem key={type.value} value={type.value}>
													{type.label}
												</SelectItem>
											))}
										</SelectContent>
									</Select>
								</div>

								{/* Contract Reference */}
								<div className="space-y-2">
									<Label className="text-sm font-medium text-gray-700 dark:text-gray-300">{t("workforce.fields.contractReference", "Contract/PO Reference")}</Label>
									<Input
										value={formData.contract_reference}
										onChange={(e) => handleInputChange("contract_reference", e.target.value)}
										placeholder={t("workforce.placeholders.contractRef", "Contract number or PO")}
										className="h-11"
									/>
								</div>

								{/* Notes */}
								<div className="space-y-2">
									<Label className="text-sm font-medium text-gray-700 dark:text-gray-300">{t("workforce.fields.notes", "Notes")}</Label>
									<Textarea
										value={formData.notes}
										onChange={(e) => handleInputChange("notes", e.target.value)}
										placeholder={t("workforce.placeholders.notes", "Additional notes...")}
										rows={4}
										className="resize-none"
									/>
								</div>
							</TabsContent>

							{/* Financial Tab */}
							<TabsContent value="financial" className="flex-1 overflow-y-auto p-4 space-y-5">
								{/* Billing Type & Currency */}
								<div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
									<div className="space-y-2">
										<Label className="text-sm font-medium text-gray-700 dark:text-gray-300">{t("workforce.fields.billingType", "Billing Type")}</Label>
										<Select
											value={formData.billing_type || ""}
											onValueChange={(value) => handleInputChange("billing_type", value)}
										>
											<SelectTrigger className="h-11">
												<SelectValue placeholder={t("workforce.placeholders.selectBilling", "Select...")} />
											</SelectTrigger>
											<SelectContent>
												{BILLING_TYPES.map((type) => (
													<SelectItem key={type.value} value={type.value}>
														{type.label}
													</SelectItem>
												))}
											</SelectContent>
										</Select>
									</div>

									<div className="space-y-2">
										<Label className="text-sm font-medium text-gray-700 dark:text-gray-300">{t("workforce.fields.currency", "Currency")}</Label>
										<Select
											value={formData.currency}
											onValueChange={(value) => handleInputChange("currency", value)}
										>
											<SelectTrigger className="h-11">
												<SelectValue />
											</SelectTrigger>
											<SelectContent>
												{CURRENCY_OPTIONS.map((currency) => (
													<SelectItem key={currency} value={currency}>
														{currency}
													</SelectItem>
												))}
											</SelectContent>
										</Select>
									</div>
								</div>

								{/* Daily Rates */}
								<Card className="border-blue-200 dark:border-blue-800 bg-blue-50/50 dark:bg-blue-900/20">
									<CardContent className="pt-4">
										<div className="flex items-center gap-2 mb-4">
											<DollarSign className="h-4 w-4 text-blue-500" />
											<span className="text-sm font-semibold text-blue-800 dark:text-blue-300">{t("workforce.fields.dailyRates", "Daily Rates")}</span>
										</div>
										<div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
											<div className="space-y-2">
												<Label className="text-sm font-medium text-gray-700 dark:text-gray-300">{t("workforce.fields.purchasePrice", "Purchase Price")}</Label>
												<Input
													type="number"
													min="0"
													step="0.01"
													value={formData.purchase_price_daily}
													onChange={(e) => handleInputChange("purchase_price_daily", e.target.value)}
													placeholder="0.00"
													className="h-11"
												/>
												<p className="text-xs text-muted-foreground">{t("workforce.fields.purchasePriceHint", "What you pay the consultant")}</p>
											</div>
											<div className="space-y-2">
												<Label className="text-sm font-medium text-gray-700 dark:text-gray-300">{t("workforce.fields.sellingPrice", "Selling Price")}</Label>
												<Input
													type="number"
													min="0"
													step="0.01"
													value={formData.selling_price_daily}
													onChange={(e) => handleInputChange("selling_price_daily", e.target.value)}
													placeholder="0.00"
													className="h-11"
												/>
												<p className="text-xs text-muted-foreground">{t("workforce.fields.sellingPriceHint", "What you charge the client")}</p>
											</div>
											<div className="space-y-2">
												<Label className="text-sm font-medium text-gray-700 dark:text-gray-300">{t("workforce.fields.margin", "Margin")}</Label>
												<div className={cn(
													"h-11 flex items-center justify-center rounded-sm border font-semibold text-lg bg-muted"
												)}>
													{dailyMargin !== null ? `${dailyMargin.toFixed(1)}%` : '-'}
												</div>
											</div>
										</div>
									</CardContent>
								</Card>

								{/* Hourly Rates */}
								<Card>
									<CardContent className="pt-4">
										<div className="flex items-center gap-2 mb-4">
											<DollarSign className="h-4 w-4 text-muted-foreground" />
											<span className="text-sm font-semibold">{t("workforce.fields.hourlyRates", "Hourly Rates")}</span>
										</div>
										<div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
											<div className="space-y-2">
												<Label className="text-sm font-medium text-gray-700 dark:text-gray-300">{t("workforce.fields.purchasePrice", "Purchase Price")}</Label>
												<Input
													type="number"
													min="0"
													step="0.01"
													value={formData.purchase_price_hourly}
													onChange={(e) => handleInputChange("purchase_price_hourly", e.target.value)}
													placeholder="0.00"
													className="h-11"
												/>
											</div>
											<div className="space-y-2">
												<Label className="text-sm font-medium text-gray-700 dark:text-gray-300">{t("workforce.fields.sellingPrice", "Selling Price")}</Label>
												<Input
													type="number"
													min="0"
													step="0.01"
													value={formData.selling_price_hourly}
													onChange={(e) => handleInputChange("selling_price_hourly", e.target.value)}
													placeholder="0.00"
													className="h-11"
												/>
											</div>
											<div className="space-y-2">
												<Label className="text-sm font-medium text-gray-700 dark:text-gray-300">{t("workforce.fields.margin", "Margin")}</Label>
												<div className="h-11 flex items-center justify-center rounded-sm border font-semibold text-lg bg-muted">
													{hourlyMargin !== null ? `${hourlyMargin.toFixed(1)}%` : '-'}
												</div>
											</div>
										</div>
									</CardContent>
								</Card>
							</TabsContent>
						</Tabs>
					</div>

					{/* Summary Sidebar - Right Side */}
					<div className={cn(
						"w-full xl:w-80 border-t xl:border-l xl:border-t-0 border-gray-200 dark:border-gray-700 bg-gradient-to-b from-gray-50 to-white dark:from-gray-800 dark:to-gray-900 min-h-0 flex flex-col",
						"xl:block",
						mobileTab === 'summary' ? "block" : "hidden"
					)}>
						<div className="p-4 space-y-4 overflow-y-auto" style={{ maxHeight: 'calc(95vh - 200px)' }}>
							<h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200 dark:text-gray-200 flex items-center gap-2">
								<TrendingUp className="h-4 w-4" />
								{t("workforce.add.summaryTitle", "Assignment Summary")}
							</h3>

							{/* Consultant Info */}
							<div className="p-3 bg-white dark:bg-gray-800 rounded-sm border border-gray-200 dark:border-gray-700 shadow-sm">
								<div className="flex items-center gap-2 mb-2">
									<div className="w-5 h-5 bg-muted rounded-sm flex items-center justify-center">
										<Users2 className="h-3 w-3 text-muted-foreground" />
									</div>
									<span className="text-sm font-semibold text-gray-800 dark:text-gray-200">{t("workforce.fields.consultant", "Consultant")}</span>
								</div>
								{selectedCandidate ? (
									<p className="text-sm text-gray-700 dark:text-gray-300">{selectedCandidate.name}</p>
								) : (
									<p className="text-sm text-gray-400 dark:text-gray-500 italic">{t("workforce.add.noConsultant", "Not selected")}</p>
								)}
							</div>

							{/* Assignment Info */}
							<div className="p-3 bg-white dark:bg-gray-800 rounded-sm border border-gray-200 dark:border-gray-700 shadow-sm">
								<div className="flex items-center gap-2 mb-2">
									<div className="w-5 h-5 bg-muted rounded-sm flex items-center justify-center">
										<Building className="h-3 w-3 text-muted-foreground" />
									</div>
									<span className="text-sm font-semibold text-gray-800 dark:text-gray-200">{t("workforce.add.clientInfo", "Client/Job")}</span>
								</div>
								{useInternalJob ? (
									selectedJob ? (
										<div>
											<p className="text-sm font-medium text-gray-700 dark:text-gray-300">{selectedJob.title}</p>
											{selectedJob.company_name && (
												<p className="text-xs text-gray-500 dark:text-gray-400">{selectedJob.company_name}</p>
											)}
										</div>
									) : (
										<p className="text-sm text-gray-400 dark:text-gray-500 italic">{t("workforce.add.noJob", "Not selected")}</p>
									)
								) : (
									formData.external_company_name ? (
										<div>
											<p className="text-sm font-medium text-gray-700 dark:text-gray-300">{formData.external_company_name}</p>
											{formData.external_job_title && (
												<p className="text-xs text-gray-500 dark:text-gray-400">{formData.external_job_title}</p>
											)}
											<Badge variant="outline" className="mt-1 text-xs">{t("workforce.add.external", "External")}</Badge>
										</div>
									) : (
										<p className="text-sm text-gray-400 dark:text-gray-500 italic">{t("workforce.add.noCompanyEntered", "Company not entered")}</p>
									)
								)}
							</div>

							{/* Timeline */}
							<div className="p-3 bg-white dark:bg-gray-800 rounded-sm border border-gray-200 dark:border-gray-700 shadow-sm">
								<div className="flex items-center gap-2 mb-2">
									<div className="w-5 h-5 bg-muted rounded-sm flex items-center justify-center">
										<Calendar className="h-3 w-3 text-muted-foreground" />
									</div>
									<span className="text-sm font-semibold text-gray-800 dark:text-gray-200">{t("workforce.add.timeline", "Timeline")}</span>
								</div>
								<div className="space-y-1 text-sm">
									<div className="flex justify-between">
										<span className="text-gray-500 dark:text-gray-400">{t("workforce.fields.startDate", "Start")}:</span>
										<span className="text-gray-700 dark:text-gray-300">{formData.start_date || '-'}</span>
									</div>
									<div className="flex justify-between">
										<span className="text-gray-500 dark:text-gray-400">{t("workforce.fields.expectedEndDate", "End")}:</span>
										<span className="text-gray-700 dark:text-gray-300">{formData.expected_end_date || t("workforce.ongoing", "Ongoing")}</span>
									</div>
								</div>
							</div>

							{/* Financial Summary */}
							{(dailyMargin !== null || hourlyMargin !== null) && (
								<div className="p-3 bg-white dark:bg-gray-800 rounded-sm border border-gray-200 dark:border-gray-700 shadow-sm">
									<div className="flex items-center gap-2 mb-2">
										<div className="w-5 h-5 bg-muted rounded-sm flex items-center justify-center">
											<TrendingUp className="h-3 w-3 text-muted-foreground" />
										</div>
										<span className="text-sm font-semibold text-gray-800 dark:text-gray-200">{t("workforce.add.financialSummary", "Margin Summary")}</span>
									</div>
									<div className="space-y-2">
										{dailyMargin !== null && (
											<div className="flex justify-between items-center">
												<span className="text-sm text-gray-500 dark:text-gray-400">{t("workforce.fields.dailyMargin", "Daily")}:</span>
												<Badge variant="secondary">
													{dailyMargin.toFixed(1)}%
												</Badge>
											</div>
										)}
										{hourlyMargin !== null && (
											<div className="flex justify-between items-center">
												<span className="text-sm text-gray-500 dark:text-gray-400">{t("workforce.fields.hourlyMargin", "Hourly")}:</span>
												<Badge variant="secondary">
													{hourlyMargin.toFixed(1)}%
												</Badge>
											</div>
										)}
									</div>
								</div>
							)}

							{/* Status */}
							<div className="p-3 bg-white dark:bg-gray-800 rounded-sm border border-gray-200 dark:border-gray-700 shadow-sm">
								<div className="flex items-center justify-between">
									<span className="text-sm text-gray-500 dark:text-gray-400">{t("workforce.fields.status", "Status")}:</span>
									<Badge variant="secondary">
										{WORKFORCE_STATUSES.find(s => s.value === formData.status)?.label || formData.status}
									</Badge>
								</div>
							</div>

							{/* Validation warnings */}
							{(!selectedCandidate || (!useInternalJob && !formData.external_company_name) || (!useInternalJob && !formData.external_job_title) || (useInternalJob && !selectedJob)) && (
								<div className="p-3 bg-muted rounded-sm border">
									<div className="flex items-start gap-2">
										<AlertTriangle className="h-4 w-4 text-muted-foreground mt-0.5" />
										<div className="text-sm">
											<p className="font-semibold">{t("workforce.add.requiredFields", "Required fields missing")}:</p>
											<ul className="list-disc list-inside mt-1 space-y-1 text-muted-foreground">
												{!selectedCandidate && <li>{t("workforce.fields.consultant", "Consultant")}</li>}
												{useInternalJob && !selectedJob && <li>{t("workforce.fields.job", "Job")}</li>}
												{!useInternalJob && !formData.external_company_name && <li>{t("workforce.fields.externalCompany", "Company name")}</li>}
												{!useInternalJob && !formData.external_job_title && <li>{t("workforce.fields.externalJobTitle", "Role/Position")}</li>}
											</ul>
										</div>
									</div>
								</div>
							)}
						</div>
					</div>
				</div>

				{/* Footer */}
				<div className="flex flex-col sm:flex-row justify-end p-3 sm:p-4 lg:p-6 border-t border-gray-200 dark:border-gray-700 gap-2 sm:gap-3 bg-gradient-to-r from-gray-50 to-white dark:from-gray-800 dark:to-gray-900 flex-shrink-0">
					<Button
						type="button"
						variant="outline"
						onClick={handleClose}
						disabled={isSubmitting}
						className="w-full sm:w-auto px-4 sm:px-6 py-2.5 sm:py-2 border-gray-300 dark:border-gray-600 hover:border-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800"
					>
						{t("common.cancel", "Cancel")}
					</Button>
					<Button
						onClick={handleSubmit}
						disabled={isSubmitting || !selectedCandidate || (useInternalJob && !selectedJob) || (!useInternalJob && !formData.external_company_name) || (!useInternalJob && !formData.external_job_title)}
						className="w-full sm:w-auto px-4 sm:px-6 py-2.5 sm:py-2"
					>
						{isSubmitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
						{t("workforce.add.submit", "Create Assignment")}
					</Button>
				</div>
			</div>
		</div>
	);
};
